/**
 * Tenant theme derivation: from primary + secondary we derive all semantic tokens
 * (e.g. text on primary, text on secondary, surfaces) so the app stays consistent
 * and accessible. All theme application goes through applyTenantTheme().
 */

import { hexToRgbaHex, getContrastTextColor } from "./styles-util";

export const DEFAULT_PRIMARY = "#33ff99";
export const DEFAULT_SECONDARY = "#162029";

/** Defaults used when tenant theme is not yet loaded (SSR or before API) */
export const DEFAULT_THEME_TOKENS = {
  "--primaryColor": DEFAULT_PRIMARY,
  "--secondaryColor": DEFAULT_SECONDARY,
  "--primaryColor10": hexToRgbaHex(DEFAULT_PRIMARY, 0.1) ?? "#33ff9919",
  "--textOnPrimary": getContrastTextColor(DEFAULT_PRIMARY),
  "--textOnSecondary": getContrastTextColor(DEFAULT_SECONDARY),
  "--textColor": "#040d13",
  "--darkTextColor": "#040d13",
  "--secondaryTextColor": "#78899f",
  "--surfaceColor": "#ffffff",
  "--surfaceMutedColor": "#f2f2f2",
  "--borderColor": "#e8e8eb",
} as const;

export type TenantThemeInput = {
  primaryColor?: string | null;
  secondaryColor?: string | null;
};

/**
 * Builds the full set of CSS variable values from tenant primary/secondary.
 * Text-on-brand colors are derived for contrast; neutrals stay fixed for consistency.
 */
export function buildTenantTheme(input: TenantThemeInput): Record<string, string> {
  const primary = input.primaryColor?.trim() || DEFAULT_PRIMARY;
  const secondary = input.secondaryColor?.trim() || DEFAULT_SECONDARY;

  const primaryNorm = primary.startsWith("#") ? primary : `#${primary}`;
  const secondaryNorm = secondary.startsWith("#") ? secondary : `#${secondary}`;

  const primary10 = hexToRgbaHex(primaryNorm, 0.1);

  return {
    "--primaryColor": primaryNorm,
    "--secondaryColor": secondaryNorm,
    "--primaryColor10": primary10 ?? `${primaryNorm}19`,
    "--textOnPrimary": getContrastTextColor(primaryNorm),
    "--textOnSecondary": getContrastTextColor(secondaryNorm),
    // Keep neutral text/surface/border as-is; they are not tenant-specific
    "--textColor": DEFAULT_THEME_TOKENS["--textColor"],
    "--darkTextColor": DEFAULT_THEME_TOKENS["--darkTextColor"],
    "--secondaryTextColor": DEFAULT_THEME_TOKENS["--secondaryTextColor"],
    "--surfaceColor": DEFAULT_THEME_TOKENS["--surfaceColor"],
    "--surfaceMutedColor": DEFAULT_THEME_TOKENS["--surfaceMutedColor"],
    "--borderColor": DEFAULT_THEME_TOKENS["--borderColor"],
  };
}

/**
 * Applies tenant theme to document.documentElement (:root).
 * Call this when company theme is loaded (e.g. in useCompanyInfo onSuccess).
 */
export function applyTenantTheme(input: TenantThemeInput): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const tokens = buildTenantTheme(input);
  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}
